import React from 'react'
import { motion } from 'framer-motion'
import Constants from '../../../app/utils/constants'
import TitleHeader from '../../atoms/TitleHeader/TitleHeader'
import colors from '../../theme/color'
import { ReactComponent as ExpertIcon } from '../../../assets/images/upskill/expert.svg'
import { ReactComponent as PlacementRateIcon } from '../../../assets/images/upskill/placementrate.svg'
import { ReactComponent as ProjectsIcon } from '../../../assets/images/upskill/projects.svg'
import { ReactComponent as AccessIcon } from '../../../assets/images/upskill/access.svg'
import { ReactComponent as LearningIcon } from '../../../assets/images/upskill/learning.svg'
import { ReactComponent as InterviewIcon } from '../../../assets/images/upskill/interview.svg'

const expertiseCardIcons = [
  { Icon: ExpertIcon },
  { Icon: PlacementRateIcon },
  { Icon: ProjectsIcon },
  { Icon: AccessIcon },
  { Icon: LearningIcon },
  { Icon: InterviewIcon },
]

const cardsContainerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.22,
      delayChildren: 0.12,
    },
  },
}

const cardItemVariants = {
  hidden: { opacity: 0, y: 56, scale: 0.94 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.9,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  },
}

const ctaContainerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.26,
      delayChildren: 0.08,
    },
  },
}

const ctaItemVariants = {
  hidden: { opacity: 0, y: 32 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.75,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  },
}

const ctaFootnoteVariants = {
  hidden: { opacity: 0, y: 10 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  },
}

const UpskillExpertiseSection = () => {
  const { upskillExpertise } = Constants
  const [hoveredCard, setHoveredCard] = React.useState<string | null>(null)
  const expertiseCards = upskillExpertise.cards.map((card, index) => ({
    ...card,
    ...expertiseCardIcons[index],
  }))

  return (
    <section className="w-full px-4 pb-16 pt-8 text-white md:px-8 md:pb-16 md:pt-10 lg:px-10 lg:pb-16 lg:pt-12 xl:px-12 min-[2560px]:pt-6" style={{ backgroundColor: colors.upskillSectionBg }}>
      <div className="mx-auto max-w-[1200px]">
        <TitleHeader
          badgeText={upskillExpertise.badge}
          titleLines={[upskillExpertise.title]}
          wrapperClassName="mb-12 flex w-full flex-col items-center text-center"
          titleClassName="font-rajdhani text-5xl md:text-[3.6rem] xl:text-[4rem]"
          badgeClassName="px-10 py-2 text-2xl md:text-3xl"
        />

        <motion.div
          className="grid grid-cols-2 gap-4 md:gap-7 xl:grid-cols-3"
          variants={cardsContainerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
        >
          {expertiseCards.map((card) => {
            const isHovered = hoveredCard === card.title
            return (
            <motion.div
              key={card.title}
              className="group relative"
              variants={cardItemVariants}
              onMouseEnter={() => setHoveredCard(card.title)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div
                aria-hidden
                className="pointer-events-none absolute left-1/2 top-[4.2rem] z-0 h-14 w-28 -translate-x-1/2 rounded-full opacity-0 blur-2xl transition-all duration-1200 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:opacity-95"
                style={{ background: colors.upskillGapGlow }}
              />
              <div className="relative z-10 mx-auto mb-4 h-[84px] w-[84px]">
                <div
                  className="flex h-full w-full items-center justify-center rounded-2xl transition-all duration-500 ease-in-out"
                  style={{
                    backgroundColor: isHovered ? colors.upskillStatCardHoverSoft : colors.upskillStatCardBase,
                    boxShadow: isHovered ? `0 12px 36px ${colors.upskillStatCardHoverShadow}` : 'none',
                  }}
                >
                  <card.Icon
                    aria-hidden
                    className={`h-[30px] w-[30px] md:h-[56px] md:w-[56px] object-contain upskill-expertise-icon ${isHovered ? 'is-hovered' : ''}`}
                  />
                </div>
              </div>
              <article
                className="relative z-10 rounded-2xl p-2 md:p-5 text-center transition-all duration-900 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:-translate-y-[2px] lg:p-6"
                style={{ backgroundColor: colors.upskillExpertiseCardBg, border: `2px solid ${colors.upskillExpertiseCardBorder}` }}
              >
                <h3 className="font-rajdhani text-xl md:text-[1.8rem] font-semibold leading-tight text-white lg:text-[1.95rem]">{card.title}</h3>
                <p className="mt-2 font-gilroy-medium text-base md:text-[1.1rem] leading-[1.35] text-white/64 lg:text-[1.2rem]">{card.description}</p>
              </article>
            </motion.div>
          )})}
        </motion.div>

        <motion.div
          className="mt-16 text-center"
          variants={ctaContainerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.35 }}
        >
          <motion.h3 className="font-rajdhani text-[2rem] font-semibold leading-[1.0] md:text-[3.5rem]" variants={ctaItemVariants}>
            {upskillExpertise.ctaTitle}
          </motion.h3>
          <motion.p className="mt-4 font-gilroy-medium text-[1.5rem] text-white/80" variants={ctaItemVariants}>
            {upskillExpertise.ctaDescription}
          </motion.p>
          <motion.div className="mt-8 flex flex-col items-center" variants={ctaItemVariants}>
            <motion.button
              type="button"
              className="inline-flex items-center justify-center rounded-full border-2 border-[var(--upskill-btn-border)] bg-transparent px-8 py-3 font-rajdhani font-semibold text-[1.45rem] leading-[1.2] text-white transition-all duration-600 ease-in hover:bg-[var(--upskill-btn-hover)] hover:text-white md:px-10 md:text-[1.6rem]"
              style={{ '--upskill-btn-border': colors.purple, '--upskill-btn-hover': colors.purple } as React.CSSProperties}
              whileHover={{ scale: 1.04, y: -1 }}
              whileTap={{ scale: 0.98 }}
            >
              {upskillExpertise.ctaButton}
            </motion.button>
            <motion.p className="mt-5 font-gilroy-medium text-[1rem] text-white/55" variants={ctaFootnoteVariants}>
              {upskillExpertise.ctaFootnote}
            </motion.p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}

export default UpskillExpertiseSection
